/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer } from "react";
import { useToggle } from "react-use";
import styled from "styled-components";
import { Box, Button, FlexContainer, theme } from "@nordcloud/gnui";
import { SearchV2Filter } from "~/generated/graphql";
import {
  ButtonGroup,
  DateRange,
  EstatePeriod,
  Granularity,
  GranularitySelector,
  PeriodSelector,
} from "~/components";
import { useQueryState } from "~/hooks";
import {
  ACTION,
  EstateChartsQueryState,
  EstateGroupBy,
  GroupingSelector,
  reducer,
} from "./Charts";
import { FiltersSidebar } from "./FiltersSidebar";
import { FiltersProp } from "./types";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
  restFilters: FiltersProp;
  disableDailyView: boolean;
  activeChart: EstateGroupBy;
  handleFilters: (filter: SearchV2Filter) => void;
  onPeriodChange: (
    selectedPeriod: EstatePeriod,
    selectedRange?: DateRange
  ) => void;
  onGranularityChange: (granularity: Granularity) => void;
  clearRange: () => void;
};

export function EstateFiltersAndChartParams({
  period,
  range,
  granularity,
  restFilters,
  disableDailyView,
  activeChart,
  handleFilters,
  onPeriodChange,
  onGranularityChange,
  clearRange,
}: Props) {
  const { updateQueryState } = useQueryState<EstateChartsQueryState>();

  const [isSidebarOpen, toggleIsSidebarOpen] = useToggle(false);

  const [filters, updateFilters] = useReducer(reducer, {
    ...restFilters,
  });

  const handleUpdate = (newFilters: FiltersProp) => {
    updateFilters({ type: ACTION.UPDATE, payload: { filters: newFilters } });
  };

  const clearFilters = () => {
    updateFilters({ type: ACTION.CLEAR });
  };

  const toggleFilters = (openFilters: boolean) => {
    handleUpdate(restFilters);
    toggleIsSidebarOpen(openFilters);
  };

  return (
    <>
      <Box boxStyle="lightGrey" marginBottom={theme.spacing.spacing04}>
        <FlexContainer justifyContent="space-between">
          <FlexContainer alignItems="center" wrap="wrap">
            <PeriodSelector
              period={period}
              range={range}
              clearRange={clearRange}
              onChange={onPeriodChange}
            />
            <GranularitySelector
              disableDailyView={disableDailyView}
              granularity={granularity}
              onChange={onGranularityChange}
            />
            <GroupingSelector
              activeChart={activeChart}
              handleGroupBySelect={(value) =>
                updateQueryState({
                  groupBy: value,
                })
              }
            />
          </FlexContainer>
          <ButtonGroup>
            <Button
              severity="low"
              icon="filter"
              onClick={() => toggleFilters(true)}
            >
              Filter
            </Button>
            <ExpandButton disabled severity="low" icon="caretDown" />
          </ButtonGroup>
        </FlexContainer>
        <FiltersSidebar
          isSidebarOpen={isSidebarOpen}
          toggleIsSidebarOpen={toggleIsSidebarOpen}
          filters={filters}
          updateFilters={handleUpdate}
          handleApply={handleFilters}
          toggleFilters={toggleFilters}
          clearFilters={clearFilters}
        />
      </Box>
    </>
  );
}

const ExpandButton = styled(Button)`
  border-top-right-radius: ${theme.radiusDefault};
  border-bottom-right-radius: ${theme.radiusDefault};
`;
